import { last, omit } from 'lodash';

import { Cell, CellViewOptions, Exploration } from '@/explore/types';
import { getCell, isConversationPart, updateViewOptions } from '@/core/cell';
import { generateShortId } from '@/lib/utils/string';

import { getCellTabId, getExplorationCellsByTab, getExplorationTabs, setCellTabId } from './tabs';

const MAX_CELLS_PER_ROW = 3;

const getCellRowId = <T extends { viewOptions?: CellViewOptions }>(cell: T) =>
  cell.viewOptions?.rowId;

export const CellDragKey = `application/x-sup-drag-cell`;
const RowIdKeyPrefix = `application/x-sup-drag-cell-row`;

export const getRowIdKey = (cell: Cell) =>
  `${RowIdKeyPrefix}-${cell.viewOptions?.rowId ?? ''}`.toLowerCase();

export const isCellDragEvent = (event: React.DragEvent) =>
  event.dataTransfer.types.indexOf(CellDragKey) !== -1;

export const isDraggedCellOnSameRow = (event: React.DragEvent, cell: Cell) =>
  event.dataTransfer.types.indexOf(getRowIdKey(cell)) !== -1;

export const canDropOnRow = (exploration: Exploration, cell: Cell, event: React.DragEvent) => {
  return (
    !isConversationPart(cell) &&
    (isDraggedCellOnSameRow(event, cell) ||
      getCellRowByCellId(exploration, cell.id).length < MAX_CELLS_PER_ROW)
  );
};

export const trackDropEvent = (
  exploration: Exploration,
  droppedCellIndex: number,
  targetIndex: number,
  droppedCell: Cell | undefined,
  afterTarget: boolean,
  mergeIntoRow: boolean,
  trackEvent: (eventName: string, properties?: { [key: string]: unknown }) => void,
) => {
  const isFromMultiCellRow =
    getCellRow(getCellRows(exploration.view.cells), droppedCellIndex).length > 1;
  const eventName =
    mergeIntoRow && !isFromMultiCellRow
      ? 'Exploration Cell Merged Into Row'
      : !mergeIntoRow && isFromMultiCellRow
        ? 'Exploration Cell Detached From Row'
        : 'Exploration Cell Moved';
  trackEvent(eventName, {
    explorationId: exploration.explorationId,
    name: exploration.name,
    cell: droppedCell,
    cellIndex: droppedCellIndex,
    targetIndex,
    afterTarget,
  });
};

/**
 * Returns a 2d array of cells, where each row is a group of cells with the same rowId.
 */
export const getCellRows = <T extends { viewOptions?: CellViewOptions }>(cells: T[]) =>
  cells.reduce((acc, cell) => {
    const lastRow = last(acc);
    if (
      lastRow === undefined ||
      getCellRowId(cell) === undefined ||
      last(lastRow)?.viewOptions?.rowId !== getCellRowId(cell)
    ) {
      return [...acc, [cell]];
    }
    return [...acc.slice(0, -1), [...lastRow, cell]];
  }, [] as T[][]);

export const getCellRow = <T>(rows: T[][], absoluteIndex: number): T[] => {
  let count = 0;
  for (const row of rows) {
    if (absoluteIndex < count + row.length) {
      return row;
    }
    count += row.length;
  }
  return [];
};

const getCellRowByCellId = (exploration: Exploration, cellId: string) => {
  const tabId = getCellTabId(exploration, cellId);
  const cells =
    tabId !== undefined ? getExplorationCellsByTab(exploration, tabId) : exploration.view.cells;
  const cellIndex = cells.findIndex((c) => c.id === cellId);
  if (cellIndex === -1) {
    return [];
  }
  return getCellRow(getCellRows(cells), cellIndex);
};

export const isCellInMultiRow = (exploration: Exploration, cellId: string) => {
  const row = getCellRowByCellId(exploration, cellId);
  return row.length > 1;
};

export const isCellFirstInRow = (exploration: Exploration, cellId: string) => {
  const row = getCellRowByCellId(exploration, cellId);
  return row.at(0)?.id === cellId;
};

export const isCellLastInRow = (exploration: Exploration, cellId: string) => {
  const row = getCellRowByCellId(exploration, cellId);
  return row.at(-1)?.id === cellId;
};

export const getRowStartIndex = (exploration: Exploration, cellId: string) => {
  const row = getCellRowByCellId(exploration, cellId);
  const firstCellId = row.at(0)?.id;
  const index = exploration.view.cells.findIndex((cell) => cell.id === firstCellId);
  return index === -1 ? 0 : index;
};

export const getRowEndIndex = (exploration: Exploration, cellId: string) => {
  const row = getCellRowByCellId(exploration, cellId);
  const lastCellId = row.at(-1)?.id;
  const index = exploration.view.cells.findIndex((cell) => cell.id === lastCellId);
  return index === -1 ? exploration.view.cells.length - 1 : index;
};

export const moveCell = (
  exploration: Exploration,
  cellId: string,
  targetId: string,
  options?: {
    after?: boolean;
    mergeIntoRow?: boolean;
    height?: number;
    targetTabId?: string;
  },
) => {
  const { after = false, mergeIntoRow = false, height, targetTabId } = options ?? {};
  const cells = exploration.view.cells.slice();

  const targetCell = getCell(cells, targetId);
  const cellIndex = cells.findIndex((cell) => cell.id === cellId);
  const targetIndex = cells.findIndex((cell) => cell.id === targetId);
  if (targetCell === undefined || cellIndex === -1 || targetIndex === -1) {
    return exploration;
  }

  const [cell] = cells.splice(cellIndex, 1);

  // Adjusted target index after to removing the cell
  const adjustedTargetIndex = targetIndex <= cellIndex ? targetIndex : targetIndex - 1;
  const cellIndexAfterMoving = adjustedTargetIndex + (after && cellIndex !== targetIndex ? 1 : 0);

  let rowId = getCellRowId(targetCell);

  if (mergeIntoRow && rowId === undefined && targetIndex !== cellIndex) {
    rowId = generateShortId();
    cells.splice(adjustedTargetIndex, 1, updateViewOptions(targetCell, { rowId }));
  }

  cells.splice(
    cellIndexAfterMoving,
    0,
    updateViewOptions(cell, { rowId: mergeIntoRow ? rowId : undefined }),
  );

  const cellHeight = mergeIntoRow
    ? (height ?? targetCell?.viewOptions?.height)
    : targetCell.viewOptions?.height;

  const updatedExploration =
    targetTabId !== undefined ? setCellTabId(exploration, cellId, targetTabId) : exploration;

  return ensureValidLayout(
    updateCellRowHeight(
      {
        ...updatedExploration,
        view: {
          ...updatedExploration.view,
          cells,
        },
      },
      targetId,
      cellHeight,
    ),
  );
};

export const moveCellUp = (exploration: Exploration, cellId: string) => {
  const rows = getCellRows(exploration.view.cells);

  if (!isCellInMultiRow(exploration, cellId)) {
    // Jump before first cell in previous row
    const previousRow = rows.at(
      rows.findIndex((row) => row.some((cell) => cell.id === cellId)) - 1,
    );
    const firstCellOfPreviousRow = previousRow?.at(0);
    if (!firstCellOfPreviousRow) {
      return exploration;
    }
    return moveCell(exploration, cellId, firstCellOfPreviousRow.id);
  } else if (isCellFirstInRow(exploration, cellId)) {
    // Detach from row = jump before itself (with mergeIntoRow = false)
    return moveCell(exploration, cellId, cellId);
    // eslint-disable-next-line no-else-return
  } else {
    // Cell is non-first in multi-row: move within row
    const row = rows.find((r) => r.some((cell) => cell.id === cellId)) ?? [];
    const previousCell = row.at(row.findIndex((cell) => cell.id === cellId) - 1);
    if (!previousCell) {
      return exploration;
    }
    return moveCell(exploration, cellId, previousCell.id, { mergeIntoRow: true });
  }
};

export const moveCellDown = (exploration: Exploration, cellId: string) => {
  const rows = getCellRows(exploration.view.cells);

  if (!isCellInMultiRow(exploration, cellId)) {
    // Jump after last cell in next row
    const nextRow = rows.at(rows.findIndex((row) => row.some((cell) => cell.id === cellId)) + 1);
    const lastCellOfNextRow = nextRow?.at(-1);
    if (!lastCellOfNextRow) {
      return exploration;
    }
    return moveCell(exploration, cellId, lastCellOfNextRow.id, { after: true });
  } else if (isCellLastInRow(exploration, cellId)) {
    // Detach from row = jump after itself (with mergeIntoRow = false)
    return moveCell(exploration, cellId, cellId, { after: true });
    // eslint-disable-next-line no-else-return
  } else {
    // Cell is non-last in multi-row: move within row
    const row = rows.find((r) => r.some((cell) => cell.id === cellId)) ?? [];
    const nextCell = row.at(row.findIndex((cell) => cell.id === cellId) + 1);
    if (!nextCell) {
      return exploration;
    }
    return moveCell(exploration, cellId, nextCell.id, { after: true, mergeIntoRow: true });
  }
};

/**
 * Cleans up row IDs for cells that are not in a multi-row layout.
 */
const cleanUpRowIds = (exploration: Exploration) => {
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: exploration.view.cells.map((cell) =>
        isCellInMultiRow(exploration, cell.id)
          ? cell
          : { ...cell, viewOptions: omit(cell.viewOptions, 'rowId') },
      ),
    },
  };
};

/**
 * Sorts cells by tab order while retaining their relative order within each tab.
 */
export const sortCellsByTabs = (exploration: Exploration) => {
  const tabs = getExplorationTabs(exploration);

  if (tabs.length === 0) {
    return exploration;
  }
  const sortedCells = tabs.flatMap((tab) => getExplorationCellsByTab(exploration, tab.tabId));

  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: sortedCells,
    },
  };
};

/**
 * Ensures that rows can only have up to MAX_CELLS_PER_ROW cells.
 * If a row has more than MAX_CELLS_PER_ROW cells, it will be split into multiple rows
 * of max MAX_CELLS_PER_ROW each.
 */
export const splitLongCellRows = (exploration: Exploration) => {
  const tabs = getExplorationTabs(exploration);
  const cellsByTab =
    tabs.length > 0
      ? tabs.map((t) => getExplorationCellsByTab(exploration, t.tabId))
      : [exploration.view.cells];

  const newCells = cellsByTab.flatMap((cells) => {
    const rows = getCellRows(cells);
    return rows.flatMap((row) => {
      if (row.length <= MAX_CELLS_PER_ROW) {
        return row;
      }
      const newRows: Cell[][] = [row.slice(0, MAX_CELLS_PER_ROW)];
      for (let i = MAX_CELLS_PER_ROW; i < row.length; i += MAX_CELLS_PER_ROW) {
        const newRowId = generateShortId();
        const rowSlice = row
          .slice(i, i + MAX_CELLS_PER_ROW)
          .map((cell) => updateViewOptions(cell, { rowId: newRowId }));
        newRows.push(rowSlice);
      }
      return newRows.flat();
    });
  });

  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: newCells,
    },
  };
};

export const ensureValidLayout = (exploration: Exploration) => {
  return cleanUpRowIds(splitLongCellRows(sortCellsByTabs(exploration)));
};

export const updateCellRowHeight = (
  exploration: Exploration,
  cellId: string,
  height: number | undefined,
) => {
  const row = getCellRowByCellId(exploration, cellId);
  const updatedCells = exploration.view.cells.map((cell) => {
    if (row.some((c) => c.id === cell.id)) {
      return updateViewOptions(cell, { height });
    }
    return cell;
  });
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: updatedCells,
    },
  };
};

export const updateCellRowCollapsed = (
  exploration: Exploration,
  cellId: string,
  collapsed: boolean,
) => {
  const row = getCellRowByCellId(exploration, cellId);
  const updatedCells = exploration.view.cells.map((cell) => {
    if (row.some((c) => c.id === cell.id)) {
      return updateViewOptions(cell, {
        collapsed: collapsed ? 'yes' : 'no',
        height: collapsed ? undefined : cell.viewOptions?.height,
      });
    }
    return cell;
  });
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: updatedCells,
    },
  };
};
